import { Robot, User } from '@phosphor-icons/react';
import { useDebounce } from 'ahooks';
import { useCallback, useEffect, useState } from 'react';
import { remove } from 'src/api/requests';
import { AutoCompleteInput } from 'src/components/AutoCompleteInput';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Badge, BadgeVariant } from 'src/components/Badge';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Heading } from 'src/components/Heading';
import { Label } from 'src/components/Label';
import Table from 'src/components/Table';
import { Tooltip } from 'src/components/Tooltip';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, Subject } from 'src/hooks/usePermissions';
import { Message } from 'src/interfaces/message.interface';
import { Number } from 'src/interfaces/number.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { StabilityLevel } from 'src/types/stability-level';
import { friendlyDateFormatter } from 'src/utils/date';
import { processPhoneNumber } from 'src/utils/number';

const headers = [
  { key: 'id', label: 'ID', width: '8rem', disableSorting: true },
  { key: 'body', label: 'Message', width: '100%', disableSorting: true },
  {
    key: 'fromNumber',
    label: 'Sender',
    width: '15%',
    wrapValue: false,
    disableSorting: true,
  },
  {
    key: 'toNumber',
    label: 'Receiver',
    width: '15%',
    wrapValue: false,
    disableSorting: true,
  },
  { key: 'createdAt', label: 'Created At', width: '15%' },
  { key: 'sentAt', label: 'Sent At', width: '15%' },
  { key: 'status', label: 'Status', width: '10%', disableSorting: true },
];

export const Messages = () => {
  const { get } = useAuthedRequest();
  const notification = useNotification();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: 'sentAt', direction: 'desc' });
  const [agentNumber, setAgentNumber] = useState<string>('');
  const [contactNumber, setContactNumber] = useState<string>('');
  const debouncedContactNumber = useDebounce(contactNumber, { wait: 500 });

  const getDisplayStatus = (message: Message) => {
    const statusBadgeVariant: Record<string, BadgeVariant> = {
      queued: 'primary',
      scheduled: 'primary',
      accepted: 'primary',
      sending: 'primary',
      delivery_unknown: 'warning',
      received: 'success',
      sent: 'success',
      delivered: 'success',
      failed: 'error',
      undelivered: 'error',
    };

    return (
      <div className="flex items-center">
        <Badge
          variant={statusBadgeVariant[message.status] || 'secondary'}
          className="flex items-center whitespace-nowrap"
        >
          {message.status}
        </Badge>
      </div>
    );
  };

  const fetchData = async (page: number, perPage: number) => {
    setLoading(true);

    const queryObject = {
      page: String(page),
      size: String(perPage),
      agent_number: agentNumber,
      contact_number: debouncedContactNumber,
      sort_desc: sortConfig.direction === 'desc' ? 'true' : 'false',
      sort_column: sortConfig.key,
    };

    const query = new URLSearchParams(queryObject as Record<string, string>);

    try {
      const fetchResult = await get(`/messages?${query.toString()}`);
      const messages = await Promise.all(
        fetchResult.items.map(async (message: Message) => {
          return {
            ...message,
            id: (
              <CopyableField
                value={message.externalId}
                notification={notification}
              />
            ),
            createdAt: friendlyDateFormatter(message.createdAt),
            sentAt: friendlyDateFormatter(message.sentAt),
            fromNumber: (
              <div className="flex gap-2 items-center">
                {processPhoneNumber(message.fromNumber)}
                {message.isOutgoing && (
                  <Tooltip content="Voice Agent" position="top">
                    <Robot className="bold w-5 h-5" />
                  </Tooltip>
                )}
                {!message.isOutgoing && (
                  <Tooltip content="Contact" position="top">
                    <User className="bold w-5 h-5" />
                  </Tooltip>
                )}
              </div>
            ),
            toNumber: (
              <div className="flex gap-2 items-center">
                {processPhoneNumber(message.toNumber)}
                {!message.isOutgoing && (
                  <Tooltip content="Voice Agent" position="top">
                    <Robot className="bold w-5 h-5" />
                  </Tooltip>
                )}
                {message.isOutgoing && (
                  <Tooltip content="Contact" position="top">
                    <User className="bold w-5 h-5" />
                  </Tooltip>
                )}
              </div>
            ),
            status: getDisplayStatus(message),
          };
        }),
      );

      setRows(messages as never[]);
      setTotalItems(fetchResult.total);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      setRows([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading && agentNumber && debouncedContactNumber) {
      fetchData(currentPage, size);
    }
  }, [currentPage, sortConfig, size, agentNumber, debouncedContactNumber]);

  const fetchLastNumbers = async (search: string) => {
    try {
      const data = await get(`/numbers/recent?number=${search}`);
      return data.map((i: { id: string; number: string }) => ({
        id: i.id,
        label: i.number,
        value: i.number,
      }));
    } catch (error) {
      console.error('Failed to fetch last numbers:', error);
      return [];
    }
  };

  const searchNumbers = useCallback(async (search: string) => {
    const queryObject: QueryObject = {
      page: '1',
      size: '50',
      filters: JSON.stringify({
        number: search,
      }),
    };

    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/numbers?${query.toString()}`);

    return data.items.map((item: Number) => ({
      value: item.number,
      label: processPhoneNumber(item.number),
    }));
  }, []);

  const handlePageChange = async (page: number, perPage: number) => {
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setSize(perPage);
    setCurrentPage(1);
    fetchData(1, perPage);
  };

  const handleSort = (key: string, direction: string) => {
    console.log(key, direction);
    setSortConfig({ key, direction });
  };

  return (
    <div className="flex-1">
      <Heading
        title="Messages"
        subtitle="Messaging history between your agents and contacts"
        stabilityLevel={StabilityLevel.Experimental}
      />
      <Can operation="read" subject={Subject.MESSAGES}>
        <ContentWrapper>
          <div className="flex items-center mb-6 gap-4">
            <div className="w-48">
              <Label required>Agent Number</Label>

              <AutoCompleteSelectBox
                defaultValue={
                  agentNumber
                    ? {
                        value: agentNumber,
                        label: processPhoneNumber(agentNumber),
                      }
                    : undefined
                }
                fetchOptions={searchNumbers}
                onChange={(value) => {
                  setAgentNumber(value);
                }}
                variant="outlined"
                color="primary"
                size="medium"
                disabled={loading}
              />
            </div>
            <div className="w-48">
              <Label required>Contact Number</Label>
              <AutoCompleteInput
                id="phone-number"
                placeholder="Contact number"
                value={contactNumber}
                onChange={(value) => {
                  setContactNumber(value);
                }}
                onRemove={(value) => remove(`/numbers/recent/${value}`)}
                required
                fetchOptions={fetchLastNumbers}
              />
            </div>
          </div>
          <Table
            headers={headers}
            rows={rows}
            totalItems={totalItems}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={handleSort}
            loading={loading}
            setSize={handleSizeChange}
            defaultSize={size}
            sortConfig={sortConfig}
          />
        </ContentWrapper>
      </Can>
    </div>
  );
};
